/**
 * Connect Service
 * API calls for Connect page data
 */

import { apiClient } from "@/lib/api-client"
import type {
  CommunityRitual,
  ConnectCommunity,
  Expert,
  FeaturedRitualist,
  HighlightedRitualist,
} from "../types/connect.types"

interface PaginatedResponse<T> {
  success: boolean
  data: {
    experts?: T[]
    communities?: T[]
    pagination: {
      page: number
      limit: number
      totalItems: number
      totalPages: number
      hasNextPage: boolean
    }
  }
  message: string
}

interface SingleResponse<T> {
  success: boolean
  data: T
  message: string
}

interface ListResponse<T> {
  success: boolean
  data: T[]
  message: string
}

/**
 * Get experts for "Book an Expert" section
 */
export async function getExperts(params?: {
  page?: number
  limit?: number
  search?: string
  city?: string
  state?: string
  country?: string
}): Promise<PaginatedResponse<Expert>["data"]> {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)
  if (params?.city) queryParams.append("city", params.city)
  if (params?.state) queryParams.append("state", params.state)
  if (params?.country) queryParams.append("country", params.country)

  const queryString = queryParams.toString()
  const url = `/api/connect/experts${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<PaginatedResponse<Expert>>(url)
  return response.data.data
}

/**
 * Get featured ritualist spotlight
 */
export async function getFeaturedRitualist(): Promise<FeaturedRitualist | null> {
  const response = await apiClient.get<SingleResponse<FeaturedRitualist | null>>(
    "/api/connect/featured-ritualist",
  )
  return response.data.data
}

/**
 * Get highlighted ritualists
 */
export async function getHighlightedRitualists(limit?: number): Promise<HighlightedRitualist[]> {
  const url = limit
    ? `/api/connect/highlighted-ritualists?limit=${limit}`
    : "/api/connect/highlighted-ritualists"

  const response = await apiClient.get<ListResponse<HighlightedRitualist>>(url)
  return response.data.data
}

/**
 * Get featured community rituals
 */
export async function getFeaturedCommunityRituals(limit?: number): Promise<CommunityRitual[]> {
  const url = limit
    ? `/api/connect/featured-community-rituals?limit=${limit}`
    : "/api/connect/featured-community-rituals"

  const response = await apiClient.get<ListResponse<CommunityRitual>>(url)
  return response.data.data
}

/**
 * Get carousel communities
 */
export async function getCarouselCommunities(limit?: number): Promise<ConnectCommunity[]> {
  const url = limit ? `/api/connect/carousel?limit=${limit}` : "/api/connect/carousel"

  const response = await apiClient.get<ListResponse<ConnectCommunity>>(url)
  return response.data.data
}

/**
 * Search communities for Explore section
 */
export async function searchCommunities(params?: {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  show?: string
  city?: string
  state?: string
  country?: string
}): Promise<PaginatedResponse<ConnectCommunity>["data"]> {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append("page", params.page.toString())
  if (params?.limit) queryParams.append("limit", params.limit.toString())
  if (params?.search) queryParams.append("search", params.search)
  if (params?.sortBy) queryParams.append("sort_by", params.sortBy)
  if (params?.show) queryParams.append("show", params.show)
  if (params?.city) queryParams.append("city", params.city)
  if (params?.state) queryParams.append("state", params.state)
  if (params?.country) queryParams.append("country", params.country)

  const queryString = queryParams.toString()
  const url = `/api/connect/communities${queryString ? `?${queryString}` : ""}`

  const response = await apiClient.get<PaginatedResponse<ConnectCommunity>>(url)
  return response.data.data
}

export async function requestHighlightRitualist(): Promise<{ message: string }> {
  const response = await apiClient.post<SingleResponse<{ message: string }>>(
    "/api/connect/highlight-ritualist-request",
  )
  return response.data.data
}

export async function requestHighlightExpert(): Promise<{ message: string }> {
  const response = await apiClient.post<SingleResponse<{ message: string }>>(
    "/api/connect/highlight-expert-request",
  )
  return response.data.data
}
